import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminSidebar from '../components/AdminSidebar';
import { getStock, addStock, getStockHistory } from '../utils/api';
import { Plus, Edit, History } from 'lucide-react';
import { toast } from 'sonner';

const AdminStock = () => {
  const [stock, setStock] = useState(null);
  const [history, setHistory] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stockRes, historyRes] = await Promise.all([
        getStock(),
        getStockHistory()
      ]);
      setStock(stockRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch stock data:', error);
      toast.error('Failed to load stock data');
    }
  };

  const handleAddStock = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      await addStock(parseFloat(amount), 'add');
      toast.success(`Added ${amount}K to stock`);
      setShowAddModal(false);
      setAmount('');
      fetchData();
    } catch (error) {
      console.error('Failed to add stock:', error);
      toast.error('Failed to add stock');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = async () => {
    if (!amount || parseFloat(amount) < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      await addStock(parseFloat(amount), 'adjust');
      toast.success(`Adjusted stock to ${amount}K`);
      setShowAdjustModal(false);
      setAmount('');
      fetchData();
    } catch (error) {
      console.error('Failed to adjust stock:', error);
      toast.error('Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0F1219]">
      <div className="scanline-overlay" />
      <AdminSidebar />

      <div className="flex-1 ml-64 p-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex justify-between items-center"
        >
          <div>
            <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-wider" data-testid="stock-title">STOCK MANAGEMENT</h1>
            <p className="text-gray-400">Manage inventory and track history</p>
          </div>
          <div className="flex gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-primary text-black font-heading font-bold tracking-wider uppercase flex items-center gap-2"
              data-testid="add-stock-button"
            >
              <Plus size={20} />
              Add Stock
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAdjustModal(true)}
              className="px-6 py-3 border border-primary text-primary hover:bg-primary/10 font-heading font-bold tracking-wider uppercase flex items-center gap-2"
              data-testid="adjust-stock-button"
            >
              <Edit size={20} />
              Adjust
            </motion.button>
          </div>
        </motion.div>

        {/* Current Stock */}
        {stock && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid md:grid-cols-3 gap-6 mb-8"
          >
            <div className="bg-card border border-border/50 p-6" data-testid="available-stock-card">
              <div className="absolute top-0 right-0 w-2 h-2 bg-primary" />
              <div className="text-gray-400 text-sm font-heading tracking-wider uppercase mb-2">Available</div>
              <div className="text-4xl font-mono font-bold text-primary">{stock.available_k}K</div>
            </div>
            <div className="bg-card border border-border/50 p-6" data-testid="reserved-stock-card">
              <div className="text-gray-400 text-sm font-heading tracking-wider uppercase mb-2">Reserved</div>
              <div className="text-4xl font-mono font-bold text-accent">{stock.reserved_k}K</div>
            </div>
            <div className="bg-card border border-border/50 p-6" data-testid="total-added-card">
              <div className="text-gray-400 text-sm font-heading tracking-wider uppercase mb-2">Total Added</div>
              <div className="text-4xl font-mono font-bold text-primary">{stock.total_added_k}K</div>
            </div>
          </motion.div>
        )}

        {/* Stock History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/50 p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <History className="text-primary" />
            <h2 className="text-2xl font-heading font-bold text-white tracking-wider">STOCK HISTORY</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="stock-history-table">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Type</th>
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Amount</th>
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Order ID</th>
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-8 text-gray-400 font-mono">
                      No history records found
                    </td>
                  </tr>
                ) : (
                  history.map((item, index) => (
                    <tr key={item.id} className="border-b border-border/30 hover:bg-primary/5 transition-colors" data-testid={`history-row-${index}`}>
                      <td className="py-4 px-4">
                        <span className={`px-3 py-1 text-xs font-mono uppercase tracking-wider ${
                          item.type === 'add' ? 'bg-primary/20 text-primary' :
                          item.type === 'reserve' ? 'bg-accent/20 text-accent' :
                          item.type === 'complete' ? 'bg-primary/20 text-primary' :
                          'bg-muted/20 text-muted-foreground'
                        }`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="py-4 px-4 font-mono text-white">{item.amount}K</td>
                      <td className="py-4 px-4 font-mono text-gray-400">{item.order_id || '-'}</td>
                      <td className="py-4 px-4 font-mono text-gray-400 text-sm">
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Add Stock Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowAddModal(false)}
          onKeyDown={(e) => e.key === 'Escape' && setShowAddModal(false)}
          role="button"
          tabIndex={0}
          aria-label="Close modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-primary/50 p-8 max-w-md w-full mx-6"
            data-testid="add-stock-modal"
          >
            <h3 className="text-2xl font-heading font-bold text-primary mb-6 tracking-wider">ADD STOCK</h3>
            <div className="mb-6">
              <label htmlFor="add-amount" className="block text-gray-400 font-heading tracking-wider uppercase mb-2 text-sm">Amount (K)</label>
              <input
                id="add-amount"
                type="number"
                step="0.1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="add-amount-input"
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleAddStock}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-primary text-black font-heading font-bold tracking-wider uppercase disabled:opacity-50"
                data-testid="confirm-add-button"
              >
                {loading ? 'ADDING...' : 'ADD'}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-6 py-3 border border-border text-gray-400 hover:text-white font-heading font-bold tracking-wider uppercase"
                data-testid="cancel-add-button"
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {showAdjustModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowAdjustModal(false)}
          onKeyDown={(e) => e.key === 'Escape' && setShowAdjustModal(false)}
          role="button"
          tabIndex={0}
          aria-label="Close modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-primary/50 p-8 max-w-md w-full mx-6"
            data-testid="adjust-stock-modal"
          >
            <h3 className="text-2xl font-heading font-bold text-primary mb-6 tracking-wider">ADJUST STOCK</h3>
            <p className="text-gray-400 text-sm mb-6">Set the available stock to a specific value</p>
            <div className="mb-6">
              <label htmlFor="adjust-amount" className="block text-gray-400 font-heading tracking-wider uppercase mb-2 text-sm">New Amount (K)</label>
              <input
                id="adjust-amount"
                type="number"
                step="0.1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="adjust-amount-input"
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleAdjustStock}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-primary text-black font-heading font-bold tracking-wider uppercase disabled:opacity-50"
                data-testid="confirm-adjust-button"
              >
                {loading ? 'ADJUSTING...' : 'ADJUST'}
              </button>
              <button
                onClick={() => setShowAdjustModal(false)}
                className="flex-1 px-6 py-3 border border-border text-gray-400 hover:text-white font-heading font-bold tracking-wider uppercase"
                data-testid="cancel-adjust-button"
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminStock;